'use client';

import type { ReactNode } from 'react';
import { createContext, useCallback, useRef, useState } from 'react';
import type { SnackbarContextValueWithAnimation, SnackbarProps, SnackbarQueueItem } from './types';

const SnackbarContext = createContext<SnackbarContextValueWithAnimation | null>(null);

interface SnackbarProviderProps {
  children: ReactNode;
  maxSnackbars?: number;
  defaultDuration?: number;
}

export function SnackbarProvider({
  children,
  maxSnackbars = 2,
  defaultDuration = 3000,
}: SnackbarProviderProps) {
  const [snackbars, setSnackbars] = useState<SnackbarQueueItem[]>([]);
  const idCounterRef = useRef(0);
  const animatedCloseRef = useRef<((id: string) => void) | null>(null);

  const generateId = useCallback(() => {
    idCounterRef.current += 1;
    return `snackbar-${idCounterRef.current}`;
  }, []);

  const close = useCallback((id: string) => {
    setSnackbars((prev) => {
      const snackbar = prev.find((s) => s.id === id);
      if (snackbar?.timerId) {
        clearTimeout(snackbar.timerId);
      }

      const filtered = prev.filter((s) => s.id !== id);

      const closedSnackbar = prev.find((s) => s.id === id);
      if (closedSnackbar?.onClose) {
        closedSnackbar.onClose();
      }

      return filtered;
    });
  }, []);

  const closeAll = useCallback(() => {
    setSnackbars((prev) => {
      prev.forEach((snackbar) => {
        if (snackbar.timerId) {
          clearTimeout(snackbar.timerId);
        }

        if (snackbar.onClose) {
          snackbar.onClose();
        }
      });
      return [];
    });
  }, []);

  const show = useCallback(
    (options: Omit<SnackbarProps, 'id'>): string => {
      const id = generateId();
      const duration = options.duration ?? defaultDuration;
      const autoClose = options.autoClose ?? true;

      const newSnackbar: SnackbarQueueItem = {
        ...options,
        id,
        createdAt: Date.now(),
        duration,
        autoClose,
      };

      if (autoClose && duration > 0) {
        newSnackbar.timerId = setTimeout(() => {
          if (animatedCloseRef.current) {
            animatedCloseRef.current(id);
          } else {
            close(id);
          }
        }, duration);
      }

      setSnackbars((prev) => {
        const updated = [...prev, newSnackbar];

        if (updated.length > maxSnackbars) {
          const toRemove = updated.slice(0, updated.length - maxSnackbars);
          toRemove.forEach((snackbar) => {
            if (snackbar.timerId) {
              clearTimeout(snackbar.timerId);
            }
            if (snackbar.onClose) {
              snackbar.onClose();
            }
          });
          return updated.slice(-maxSnackbars);
        }

        return updated;
      });

      return id;
    },
    [generateId, defaultDuration, maxSnackbars, close],
  );

  const setAnimatedClose = useCallback((animatedCloseFn: (id: string) => void) => {
    animatedCloseRef.current = animatedCloseFn;
  }, []);

  const contextValue: SnackbarContextValueWithAnimation = {
    show,
    close,
    closeAll,
    snackbars: snackbars.map(({ timerId, createdAt, ...rest }) => rest),
    setAnimatedClose,
  };

  return <SnackbarContext.Provider value={contextValue}>{children}</SnackbarContext.Provider>;
}

export { SnackbarContext };
